import axios from 'axios'

import { PRODUCT_URL } from '@/shared/lib/const/env'

import { ProductReview, ProductReviewRatings, ProductReviewsApiError, ReviewMediaItem } from '../model/types'

export type ApiRecord = Record<string, unknown>

export const asRecord = (value: unknown): ApiRecord => (value && typeof value === 'object' ? (value as ApiRecord) : {})
export const asNumber = (value: unknown) => (Number.isFinite(Number(value)) ? Number(value) : 0)
const asString = (value: unknown) => (typeof value === 'string' ? value : '')
const getPayloadItems = (payload: unknown): ApiRecord[] => {
	const root = asRecord(payload)
	const data = root.data
	if (Array.isArray(payload)) return payload.map(asRecord)
	if (Array.isArray(data)) return data.map(asRecord)

	const nestedData = asRecord(data).data
	return Array.isArray(nestedData) ? nestedData.map(asRecord) : []
}

export const getPayloadMeta = (payload: unknown) => {
	const root = asRecord(payload)
	return asRecord(root.meta ?? asRecord(root.data).meta)
}

const normalizeExternalUrl = (url: string) => {
	if (!url) return ''
	return /^https?:\/\//.test(url) ? url : `https://${url}`
}
const isTemplateHref = (href: string) => href.includes('[') || href.includes(']')
const getReviewShopUrl = (item: ApiRecord, project: ApiRecord) =>
	normalizeExternalUrl(
		asString(
			item.shop_url ??
				item.source_url ??
				item.partner_url ??
				item.resource_url ??
				item.url ??
				item.link ??
				project.url ??
				project.link
		)
	)

const getReviewProductHref = (item: ApiRecord) => {
	const product = asRecord(item.product ?? item.item)
	const href = asString(
		item.product_url ??
			item.product_link ??
			item.detail_page_url ??
			item.detailPageUrl ??
			product.url ??
			product.link ??
			product.detail_page_url
	)
	const code = asNumber(item.code ?? product.code ?? product.kod ?? product.KOD)

	return href && !isTemplateHref(href) ? href : code ? `${PRODUCT_URL}${code}/` : undefined
}

export const addReviewQueryParams = (href: string, reviewId: string) => {
	if (!href) return ''

	const separator = href.includes('?') ? '&' : '?'
	return `${href}${separator}tab=reviews&reviewId=${reviewId}`
}

const getReviewPartnerHref = (item: ApiRecord, project: ApiRecord) => {
	const reviewId = String(item.id ?? item.reviews_id ?? '')
	const productHref = getReviewProductHref(item)
	const shopUrl = getReviewShopUrl(item, project)
	if (!reviewId || !productHref || !shopUrl) return shopUrl

	const href = productHref.startsWith('http')
		? productHref
		: `${shopUrl}${productHref.startsWith('/') ? '' : '/'}${productHref}`
	return addReviewQueryParams(href, reviewId)
}

const normalizeReviewAuthor = (value: unknown) => {
	const author = asString(value).trim()
	return !author || author === 'Имя закрыто' ? 'Имя скрыто' : author
}

const getValidationMessage = (data: unknown): string | undefined => {
	if (!data || typeof data !== 'object') return undefined
	const response = data as Record<string, unknown>
	if (typeof response.message === 'string') return response.message
	if (typeof response.error === 'string') return response.error

	const errors = response.errors
	if (!errors || typeof errors !== 'object') return undefined
	const firstError = Object.values(errors as Record<string, unknown[]>)
		.flat()
		.find(Boolean)
	return typeof firstError === 'string' ? firstError : undefined
}

export const normalizeProductReviewsError = (error: unknown): ProductReviewsApiError => {
	if (!axios.isAxiosError(error)) return new ProductReviewsApiError('Не удалось выполнить запрос отзывов')
	const status = error.response?.status
	return new ProductReviewsApiError(
		status === 422 ? 'Проверьте заполнение формы' : 'Сервис отзывов временно недоступен',
		status,
		getValidationMessage(error.response?.data)
	)
}

export const getReviewCardItems = getPayloadItems

export const normalizeReviewCard = (item: ApiRecord): ProductReview => {
	const media = Array.isArray(item.media) ? item.media.map(asRecord) : []
	const mediaUrls = media.map(item => asString(item.original_url)).filter(Boolean)
	const reviewMedia = media
		.map(item => ({
			uuid: asString(item.uuid ?? item.id),
			src: asString(item.original_url ?? item.url ?? item.src ?? item.path)
		}))
		.filter(item => item.uuid && item.src)
	const photos = Array.isArray(item.photos) ? item.photos.map(asString).filter(Boolean) : mediaUrls
	const video = asString(item.video)
	const reactionValue = item.has_reaction ?? item.user_reaction ?? item.reaction
	const reaction = typeof reactionValue === 'object' ? asString(asRecord(reactionValue).type) : asString(reactionValue)
	const project = asRecord(item.project)
	const source = asString(item.shop ?? item.source ?? project.system_name)
	const shop = source === 'samsungstore' ? 'samsung' : source === 'h-stores' ? 'h-store' : source
	const normalizedShop = shop === 'h-store' || shop === 'iport' || shop === 'samsung' ? shop : undefined

	return {
		id: String(item.id ?? item.reviews_id ?? ''),
		productCode: asNumber(item.code) || undefined,
		productHref: getReviewProductHref(item),
		isUserReview: item.is_user_review === true || item.is_user_review === 1,
		verified: item.verified === true || item.verified === 1,
		active: item.active !== false,
		author: normalizeReviewAuthor(item.author ?? item.user_name ?? item.name),
		createdAt: asString(item.created_at ?? item.createdAt),
		rating: asNumber(item.rating),
		comment: asString(item.content ?? item.comment),
		photos: video ? [...photos, video] : photos,
		media: reviewMedia,
		likes: asNumber(item.likes ?? item.likes_count),
		dislikes: asNumber(item.dislikes ?? item.dislikes_count),
		isCurrentVariant: true,
		shop: normalizedShop,
		shopUrl: getReviewPartnerHref(item, project) || undefined,
		userVote: reaction === 'like' || reaction === 'dislike' ? reaction : null
	}
}

const getReviewId = (item: ApiRecord, review: ProductReview) => {
	const nestedReview = asRecord(item.review ?? item.reviews ?? item.card ?? item.review_card)
	return String(item.reviews_id ?? item.review_id ?? item.reviewId ?? nestedReview.id ?? review.id)
}

export const normalizeReviewMediaItems = (payload: unknown): ReviewMediaItem[] => {
	const photoIndexesByReviewId = new Map<string, number>()

	return getReviewCardItems(payload).flatMap((item, itemIndex) => {
		const nestedReview = asRecord(item.review ?? item.reviews ?? item.card ?? item.review_card)
		const normalizedReview = normalizeReviewCard(Object.keys(nestedReview).length ? nestedReview : item)
		const reviewId = getReviewId(item, normalizedReview)
		const review = { ...normalizedReview, id: reviewId || normalizedReview.id }
		const media = Array.isArray(item.media) ? item.media.map(asRecord) : []
		const mediaSources = [
			asString(item.original_url ?? item.url ?? item.src ?? item.path ?? item.file),
			...(Array.isArray(item.photos) ? item.photos.map(asString) : []),
			...media.map(mediaItem => asString(mediaItem.original_url ?? mediaItem.url ?? mediaItem.src ?? mediaItem.path)),
			asString(item.video)
		].filter(Boolean)

		return mediaSources.map((src, mediaSourceIndex) => {
			const photoIndex = photoIndexesByReviewId.get(reviewId) ?? 0
			photoIndexesByReviewId.set(reviewId, photoIndex + 1)

			return {
				id: `${String(item.id ?? `${reviewId}-${itemIndex}`)}-${mediaSourceIndex}`,
				src,
				photoIndex,
				review,
				reviewId
			}
		})
	})
}

export const normalizeProductRatings = (payload: unknown): ProductReviewRatings => {
	return getPayloadItems(payload).reduce<ProductReviewRatings>((result, item) => {
		const code = String(item.code ?? item.product_code ?? '')
		if (!code) return result
		result[code] = {
			rating: asNumber(item.rating ?? item.average ?? item.average_rating),
			count: asNumber(item.count ?? item.total ?? item.reviews_count)
		}
		return result
	}, {})
}
